import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { visibilityAPI } from '../services/api';
import './VisibilityBubble.css';

interface VisibilityBubbleProps {
  friendId: number;
  addressId: number;
  currentLevel: number | null; // null = using default
  defaultLevel: number;
  hasCustomRule: boolean;
  onVisibilityChange?: (newLevel: number | null) => void;
}

const VISIBILITY_LEVELS = [1, 2, 3, 4, 5];

const VisibilityBubble: React.FC<VisibilityBubbleProps> = ({
  friendId,
  addressId,
  currentLevel,
  defaultLevel,
  hasCustomRule,
  onVisibilityChange,
}) => {
  const { t } = useLanguage();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false); // For collapse animation
  const [selectedLevel, setSelectedLevel] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const animationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // The effective level shown (custom or default)
  const effectiveLevel = currentLevel ?? defaultLevel;

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
    };
  }, []);

  // Reset selected level when closing
  useEffect(() => {
    if (!isExpanded && !isAnimating) {
      setSelectedLevel(null);
    }
  }, [isExpanded, isAnimating]);

  const handleExpand = () => {
    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current);
    }
    setIsAnimating(false);
    setIsExpanded(true);
  };

  const handleCollapse = () => {
    setIsAnimating(true);
    // Wait for collapse animation to complete before hiding
    animationTimeoutRef.current = setTimeout(() => {
      setIsExpanded(false);
      setIsAnimating(false);
    }, 250); // Match CSS animation duration
  };

  const handleBubbleClick = () => {
    if (isExpanded || isAnimating) {
      handleCollapse();
    } else {
      handleExpand();
    }
  };

  const handleLevelClick = (level: number) => {
    if (level === effectiveLevel && selectedLevel === null) {
      // Clicking on current level without having selected another = close
      handleCollapse();
    } else if (level === effectiveLevel && selectedLevel !== null) {
      // Clicking on original level after selecting another = cancel selection
      setSelectedLevel(null);
    } else {
      // Select a different level
      setSelectedLevel(level);
    }
  };

  const handleSave = async () => {
    if (selectedLevel === null) return;

    setIsSaving(true);
    try {
      if (selectedLevel === defaultLevel && hasCustomRule) {
        // Delete custom rule to revert to default
        await visibilityAPI.deleteIndividualVisibility('permanent', addressId, friendId);
        onVisibilityChange?.(null);
      } else {
        // Set custom rule
        await visibilityAPI.setIndividualVisibility('permanent', addressId, friendId, selectedLevel);
        onVisibilityChange?.(selectedLevel);
      }
      handleCollapse();
      setSelectedLevel(null);
    } catch (error) {
      console.error('Failed to save visibility:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const getDisplayLevel = () => {
    if (selectedLevel !== null) return selectedLevel;
    return effectiveLevel;
  };

  const hasChanges = selectedLevel !== null && selectedLevel !== effectiveLevel;

  // Show expanded view if expanded OR animating (collapsing)
  const showExpanded = isExpanded || isAnimating;

  return (
    <div className={`visibility-bubble-container ${isExpanded ? 'expanded' : ''} ${isAnimating ? 'collapsing' : ''}`}>
      {/* Main bubble or expanded bubbles */}
      <div className="visibility-bubbles">
        {showExpanded ? (
          // Expanded: show all levels
          VISIBILITY_LEVELS.map((level, index) => {
            const isActive = level === getDisplayLevel();
            const isOriginal = level === effectiveLevel && selectedLevel === null;
            const isSelected = level === selectedLevel;

            return (
              <button
                key={level}
                className={`visibility-level-btn ${isActive ? 'active' : ''} ${isSelected ? 'selected' : ''} ${isOriginal ? 'original' : ''}`}
                onClick={() => handleLevelClick(level)}
                disabled={isSaving || isAnimating}
                style={{ '--bubble-index': index } as React.CSSProperties}
              >
                {level}
              </button>
            );
          })
        ) : (
          // Collapsed: show single bubble
          <button
            className={`visibility-level-btn active ${hasCustomRule ? 'custom' : 'default'}`}
            onClick={handleBubbleClick}
            title={t('friends.visibility.clickToChange')}
          >
            {effectiveLevel}
          </button>
        )}
      </div>

      {/* Description and save button (only when expanded and changed) */}
      {showExpanded && (
        <div className={`visibility-details ${isAnimating ? 'collapsing' : ''}`}>
          <p className="visibility-description">
            {t(`addressPrivacy.levels.${getDisplayLevel()}.description`)}
          </p>
          {hasChanges && !isAnimating && (
            <button
              className="btn-save-visibility"
              onClick={handleSave}
              disabled={isSaving}
              title={t('common.save')}
            >
              {isSaving ? '...' : (
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
                  <polyline points="17 21 17 13 7 13 7 21"/>
                  <polyline points="7 3 7 8 15 8"/>
                </svg>
              )}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default VisibilityBubble;
